<template>
	<div class="user-center-container">
		<div class="user-center-content">
			<tab-pane v-model="activeTab" :tabs="tabs" :pane-style="invoiceTabPaneStyle" @change="handleTabChange">
				<template slot="extra">
					<el-button class="invoice-title-entry" type="primary" plain @click="handleOpenInvoiceTitle">
						<i class="el-icon-document"></i>
						发票抬头管理
					</el-button>
				</template>

				<div class="search-container invoice-search-container">
					<div class="search-form">
						<div class="form-item">
							<span class="label">订单编号</span>
							<el-input
								v-model.trim="queryParams.orderCode"
								placeholder="请输入订单编号"
								clearable
								@keyup.enter.native="handleSearch"
							></el-input>
						</div>
						<div class="form-item">
							<span class="label">订单时间</span>
							<el-date-picker
								v-model="queryParams.orderTime"
								type="date"
								placeholder="请选择订单时间"
								value-format="yyyy-MM-dd"
								clearable
							></el-date-picker>
						</div>
						<div class="form-buttons">
							<el-button type="primary" @click="handleSearch">查询</el-button>
							<el-button @click="handleResetSearch">重置</el-button>
						</div>
					</div>
				</div>

				<div v-loading="loading" class="invoice-list">
					<div v-if="!loading && pageOptions.total" class="list-summary">
						共
						<strong>{{ pageOptions.total }}</strong>
						条发票记录
					</div>
					<template v-if="invoiceList.length">
						<div v-for="item in invoiceList" :key="item.id || item.orderCode" class="invoice-item">
							<div class="item-header">
								<div class="order-info">
									<span>申请编号：{{ item.applicationCode || item.orderCode || '--' }}</span>
									<span>订单号：{{ formatOrderCodes(item) }}</span>
									<span>申请时间：{{ item.createTime || '--' }}</span>
									<span v-if="item.invoiceTime">开票时间：{{ item.invoiceTime }}</span>
								</div>
								<span class="invoice-status" :class="getInvoiceStateClass(item.invoiceState)">
									{{ getInvoiceStateName(item.invoiceState) }}
								</span>
							</div>
							<div class="item-content">
								<div class="invoice-info">
									<div class="invoice-name">{{ item.productName || item.setMealName || item.itemName || '订单发票' }}</div>
									<div class="invoice-meta">
										<span>发票类型：{{ getInvoiceTypeName(item.invoiceType) }}</span>
										<span>开票主体：{{ item.issuerName || '楚建通平台' }}</span>
										<span>关联订单：{{ item.orderCount || 1 }} 笔</span>
									</div>
								</div>
								<div class="invoice-amount">
									<span>发票金额</span>
									<strong>
										<small>¥</small>
										{{ formatAmount(item.amount) }}
									</strong>
								</div>
								<div class="action-buttons">
									<template v-if="isInvoiceCompleted(item.invoiceState)">
										<el-button type="text" icon="el-icon-view" @click="handleView(item)">查看发票</el-button>
										<el-button
											type="text"
											icon="el-icon-download"
											:loading="downloadingFileRecordId === item.fileId"
											@click="handleDownload(item)"
										>
											下载发票
										</el-button>
									</template>
									<el-button
										v-else-if="isInvoicePending(item.invoiceState)"
										type="primary"
										size="small"
										@click="handleApplyInvoice(item)"
									>
										申请开票
									</el-button>
								</div>
							</div>
						</div>
					</template>
					<empty-content v-else empty-text="暂无发票信息"></empty-content>

					<jr-pagination
						v-if="pageOptions.total > pageOptions.limit"
						:limit.sync="pageOptions.limit"
						:page.sync="pageOptions.page"
						:total="pageOptions.total"
						layout="prev, pager, next"
						prev-text="上一页"
						next-text="下一页"
						@change="handlePaginationChange"
					></jr-pagination>
				</div>
			</tab-pane>
		</div>

		<el-dialog
			:visible.sync="previewVisible"
			custom-class="user-invoice-preview-dialog"
			width="900px"
			top="5vh"
			append-to-body
			:close-on-click-modal="false"
			@closed="handlePreviewClosed"
		>
			<div slot="title" class="preview-dialog-title">
				<span class="preview-title-icon"><i class="el-icon-tickets"></i></span>
				<div class="preview-title-info">
					<div class="preview-title-text">发票预览</div>
					<div v-if="previewFileName" class="preview-file-name">{{ previewFileName }}</div>
				</div>
			</div>
			<div v-loading="previewLoading" class="preview-content" element-loading-text="发票加载中...">
				<img
					v-if="previewType === 'image' && previewUrl"
					:src="previewUrl"
					alt="发票预览"
					class="preview-image"
					@load="handlePreviewLoaded"
					@error="handlePreviewError"
				/>
				<iframe
					v-else-if="previewType === 'pdf' && previewUrl"
					:src="previewUrl"
					title="发票预览"
					class="preview-frame"
					@load="handlePreviewLoaded"
				></iframe>
				<div v-else-if="!previewLoading" class="preview-empty">
					<i :class="previewError ? 'el-icon-warning-outline' : 'el-icon-document-delete'"></i>
					<span>{{ previewError || '暂无可预览的发票文件' }}</span>
				</div>
			</div>
			<div slot="footer" class="preview-dialog-footer">
				<el-button size="small" @click="previewVisible = false">关闭</el-button>
				<el-button
					size="small"
					type="primary"
					icon="el-icon-download"
					:loading="downloadingFileRecordId === previewFileRecordId"
					@click="handleDownloadFile(previewFileRecordId, previewFileName)"
				>
					下载发票
				</el-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>
	import axios from 'axios'
	import TabPane from '@/components/TabPane'
	import JrPagination from '@/components/Pagination'
	import { queryInvoiceRecordPager, downloadFileRecord, previewFileRecord } from '@/api/memberBenefit'
	import { ENUM_INVOICE_TYPE_STATE, INVOICE_STATE, INVOICE_STATE_CLASS } from '@/enum'
	import { getToken } from '@/utils/auth'

	export default {
		name: 'MyInvoice',
		components: {
			TabPane,
			JrPagination
		},
		data() {
			return {
				activeTab: 'all',
				invoiceTabPaneStyle: {
					'--tab-pane-header-padding-bottom': '5px',
					'--tab-pane-header-margin-bottom': '10px',
					'--tab-pane-item-font-size': '16px',
					'--tab-pane-indicator-bottom': '-5px'
				},
				tabs: [
					{ name: '全部', value: 'all' },
					{ name: '已开票', value: 'completed' },
					{ name: '开票中', value: 'processing' },
					{ name: '未开票', value: 'pending' }
				],
				loading: false,
				invoiceList: [],
				queryParams: {
					orderCode: '',
					orderTime: ''
				},
				pageOptions: {
					page: 1,
					limit: 5,
					total: 0
				},
				previewVisible: false,
				previewLoading: false,
				previewUrl: '',
				previewType: '',
				previewError: '',
				previewFileName: '',
				previewFileRecordId: '',
				previewRequestVersion: 0,
				downloadingFileRecordId: ''
			}
		},
		mounted() {
			this.queryInvoiceList()
		},
		beforeDestroy() {
			this.previewRequestVersion++
			this.releasePreviewUrl()
		},
		methods: {
			formatAmount(value) {
				const amount = Number(value)
				return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
			},
			formatOrderCodes(item) {
				const orderItems = Array.isArray(item.orderItems) ? item.orderItems : []
				if (!orderItems.length) return item.orderCode || '--'
				return (
					orderItems
						.map(order => order.orderCode)
						.filter(Boolean)
						.join('、') || '--'
				)
			},
			getInvoiceStateFilter() {
				return {
					completed: INVOICE_STATE.COMPLETED,
					processing: INVOICE_STATE.PROCESSING,
					pending: INVOICE_STATE.PENDING
				}[this.activeTab]
			},
			queryInvoiceList() {
				this.loading = true
				const params = {
					page: this.pageOptions.page,
					size: this.pageOptions.limit,
					...this.queryParams
				}
				const invoiceState = this.getInvoiceStateFilter()
				if (invoiceState !== undefined) {
					params.invoiceState = invoiceState
				}

				queryInvoiceRecordPager(params)
					.then(response => {
						const data = response.data || {}
						this.invoiceList = data.records || []
						this.pageOptions.total = data.total || 0
					})
					.catch(() => {
						this.invoiceList = []
						this.pageOptions.total = 0
					})
					.finally(() => {
						this.loading = false
					})
			},
			handleTabChange() {
				this.pageOptions.page = 1
				this.queryInvoiceList()
			},
			handleSearch() {
				this.pageOptions.page = 1
				this.queryInvoiceList()
			},
			handleResetSearch() {
				this.queryParams = {
					orderCode: '',
					orderTime: ''
				}
				this.pageOptions.page = 1
				this.queryInvoiceList()
			},
			handlePaginationChange({ page, limit }) {
				this.pageOptions.page = page
				this.pageOptions.limit = limit
				this.queryInvoiceList()
			},
			getInvoiceStateName(state) {
				return (
					{
						[INVOICE_STATE.PENDING]: '未开票',
						[INVOICE_STATE.COMPLETED]: '已开票',
						[INVOICE_STATE.PROCESSING]: '开票中'
					}[state] || '未知状态'
				)
			},
			getInvoiceStateClass(state) {
				return INVOICE_STATE_CLASS[state] || ''
			},
			getInvoiceTypeName(type) {
				return ENUM_INVOICE_TYPE_STATE[type] || '--'
			},
			isInvoiceCompleted(state) {
				return Number(state) === INVOICE_STATE.COMPLETED
			},
			isInvoicePending(state) {
				return Number(state) === INVOICE_STATE.PENDING
			},
			handleOpenInvoiceTitle() {
				this.$router.push('/user/invoiceManagement/invoiceTitle')
			},
			handleApplyInvoice(item) {
				this.$router.push({
					path: '/user/market/order/select',
					query: { orderId: item.orderId || item.id }
				})
			},
			handleView(item) {
				if (!item || !item.fileId) {
					this.$message.warning('暂无可预览的发票文件')
					return
				}
				this.releasePreviewUrl()
				this.previewFileRecordId = item.fileId
				this.previewFileName = item.fileName || item.invoiceFileName || '电子发票'
				this.previewLoading = true
				this.previewType = ''
				this.previewError = ''
				this.previewVisible = true
				const requestVersion = ++this.previewRequestVersion

				axios
					.get(previewFileRecord({ fileRecordId: item.fileId, InternetToken: getToken() }), {
						responseType: 'blob'
					})
					.then(response => {
						if (requestVersion !== this.previewRequestVersion) return
						const contentType = (response.headers['content-type'] || response.data.type || '').toLowerCase()
						if (contentType.indexOf('image/') === 0) {
							this.previewType = 'image'
						} else if (contentType.indexOf('application/pdf') === 0) {
							this.previewType = 'pdf'
						} else {
							this.previewError = '该发票格式暂不支持在线预览，请下载后查看'
							this.previewLoading = false
							return
						}
						this.previewUrl = URL.createObjectURL(response.data)
					})
					.catch(() => {
						if (requestVersion !== this.previewRequestVersion) return
						this.previewError = '发票加载失败，请稍后重试或下载后查看'
						this.previewLoading = false
					})
			},
			handlePreviewLoaded() {
				this.previewLoading = false
			},
			handlePreviewError() {
				this.previewError = '发票加载失败，请稍后重试或下载后查看'
				this.previewType = ''
				this.previewLoading = false
				this.releasePreviewUrl()
			},
			handlePreviewClosed() {
				this.previewRequestVersion++
				this.releasePreviewUrl()
				this.previewType = ''
				this.previewError = ''
				this.previewFileName = ''
				this.previewFileRecordId = ''
				this.previewLoading = false
			},
			releasePreviewUrl() {
				if (this.previewUrl) {
					URL.revokeObjectURL(this.previewUrl)
					this.previewUrl = ''
				}
			},
			handleDownload(item) {
				this.handleDownloadFile(item && item.fileId, item && (item.fileName || item.invoiceFileName))
			},
			async handleDownloadFile(fileRecordId, fileName) {
				if (!fileRecordId) {
					this.$message.warning('暂无可下载的发票文件')
					return
				}
				if (this.downloadingFileRecordId) return
				this.downloadingFileRecordId = fileRecordId
				try {
					const response = await axios.get(downloadFileRecord({ fileRecordId, InternetToken: getToken() }), {
						responseType: 'blob'
					})
					const contentType = (response.headers['content-type'] || response.data.type || '').toLowerCase()
					const downloadFileName =
						fileName && fileName !== '电子发票' ? fileName : `电子发票${this.getFileExtension(contentType)}`
					const objectUrl = URL.createObjectURL(response.data)
					const link = document.createElement('a')
					link.style.display = 'none'
					link.href = objectUrl
					link.download = downloadFileName
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
					this.$message.success('发票下载已开始')
				} catch (error) {
					this.$message.error('发票下载失败，请稍后重试')
				} finally {
					this.downloadingFileRecordId = ''
				}
			},
			getFileExtension(contentType) {
				const extensionMap = {
					'application/pdf': '.pdf',
					'image/jpeg': '.jpg',
					'image/jpg': '.jpg',
					'image/png': '.png'
				}
				return extensionMap[contentType.split(';')[0]] || ''
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import '~@/views/user/styles/user-center.scss';
	@import '~@/styles/element-reset.scss';

	.user-center-container {
		background: #f5f6f8;
	}

	.user-center-content {
		padding: 20px 22px 24px;
	}

	.invoice-title-entry {
		border-radius: 4px;
		font-weight: 500;

		i {
			margin-right: 4px;
		}
	}

	.invoice-search-container {
		margin-top: 8px;
		padding: 14px 16px;
		background: #f7f8fa;
		border: 1px solid #ebeef2;
		border-radius: 4px;

		.search-form {
			align-items: center;
			gap: 16px 24px;
		}

		::v-deep {
			.el-input__inner {
				background: #fff;
				border-color: #dcdfe6;
				border-radius: 4px !important;
			}

			.form-buttons .el-button {
				border-radius: 4px;
				font-size: 14px;
				padding: 9px 20px;
			}
		}
	}

	.invoice-list {
		min-height: 320px;
		padding-top: 16px;
	}

	.list-summary {
		margin-bottom: 10px;
		color: #909399;
		font-size: 13px;
		line-height: 20px;

		strong {
			color: #2671cb;
			font-weight: 600;
		}
	}

	.invoice-item {
		margin-bottom: 14px;
		overflow: hidden;
		background: #fff;
		border: 1px solid #e2e6ec;
		border-radius: 5px;
		transition: border-color 0.2s, box-shadow 0.2s;

		&:hover {
			border-color: #c8d8ea;
			box-shadow: 0 5px 16px rgba(41, 72, 107, 0.09);
		}

		.item-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 20px;
			min-height: 44px;
			padding: 9px 16px;
			background: #f7f8fa;
			border-bottom: 1px solid #ebeef2;

			.order-info {
				display: flex;
				flex-wrap: wrap;
				gap: 5px 24px;
				min-width: 0;
			}

			.order-info span {
				color: #606266;
				font-size: 13px;
				line-height: 24px;
			}
		}

		.item-content {
			display: flex;
			align-items: center;
			gap: 24px;
			min-height: 104px;
			padding: 18px 20px;
		}

		.invoice-info {
			flex: 1;
			min-width: 0;
		}

		.invoice-name {
			margin-bottom: 12px;
			overflow: hidden;
			font-size: 16px;
			font-weight: 600;
			line-height: 24px;
			color: #303133;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.invoice-meta {
			display: flex;
			flex-wrap: wrap;
			gap: 6px 26px;

			span {
				color: #7b8490;
				font-size: 13px;
				line-height: 20px;
			}
		}

		.invoice-amount {
			display: flex;
			flex: 0 0 130px;
			flex-direction: column;
			align-items: flex-end;
			padding-right: 22px;
			border-right: 1px solid #ebeef2;

			span {
				margin-bottom: 5px;
				color: #909399;
				font-size: 12px;
			}

			strong {
				color: #e25335;
				font-size: 20px;
				font-weight: 600;
				line-height: 28px;
			}

			small {
				margin-right: 2px;
				font-size: 13px;
			}
		}

		.action-buttons {
			display: flex;
			flex: 0 0 174px;
			align-items: center;
			justify-content: flex-end;
			white-space: nowrap;

			::v-deep .el-button--text {
				padding: 8px 3px;
				font-weight: 500;
			}
		}

		.invoice-status {
			flex: 0 0 auto;
			padding: 3px 10px;
			font-size: 12px;
			line-height: 20px;
			border-radius: 12px;

			&.completed {
				color: #23834a;
				background: #eaf7ef;
			}

			&.processing {
				color: #d89017;
				background: #fff7e8;
			}

			&.pending {
				color: #777777;
				background: #f1f2f4;
			}
		}
	}

	.preview-dialog-title {
		display: flex;
		align-items: center;
		min-height: 34px;
		padding-right: 42px;
	}

	.preview-title-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 36px;
		width: 36px;
		height: 36px;
		margin-right: 11px;
		color: #2671cb;
		font-size: 18px;
		background: #eaf2fb;
		border-radius: 6px;
	}

	.preview-title-info {
		min-width: 0;
	}

	.preview-title-text {
		color: #303133;
		font-size: 16px;
		font-weight: 600;
		line-height: 22px;
	}

	.preview-file-name {
		max-width: 700px;
		overflow: hidden;
		color: #909399;
		font-size: 12px;
		line-height: 18px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.preview-content {
		height: calc(90vh - 154px);
		min-height: 420px;
		max-height: 680px;
		overflow: hidden;
		background: #fff;
		border: 1px solid #dfe4ea;
		border-radius: 5px;
	}

	.preview-frame {
		display: block;
		width: 100%;
		height: 100%;
		background: #fff;
		border: 0;
	}

	.preview-image {
		display: block;
		width: 100%;
		height: 100%;
		padding: 12px;
		object-fit: contain;
		box-sizing: border-box;
		background: #fff;
	}

	.preview-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		color: #909399;

		i {
			margin-bottom: 12px;
			color: #c0c4cc;
			font-size: 44px;
		}
	}

	.preview-dialog-footer {
		display: flex;
		justify-content: flex-end;
	}

	@media (max-width: 980px) {
		.invoice-item .item-content {
			flex-wrap: wrap;
		}

		.invoice-item .invoice-amount {
			align-items: flex-start;
			border-right: 0;
		}

		.invoice-item .action-buttons {
			flex: 1;
		}
	}

	@media (max-width: 768px) {
		.user-center-content {
			padding: 16px;
		}

		.invoice-search-container .search-form {
			align-items: stretch;
			flex-direction: column;
		}

		.invoice-item .item-header {
			align-items: flex-start;
		}

		.invoice-item .invoice-amount {
			flex-basis: auto;
			padding-right: 0;
		}

		.invoice-item .action-buttons {
			flex-basis: auto;
		}

		.preview-content {
			min-height: 360px;
		}

		.preview-file-name {
			max-width: calc(100vw - 180px);
		}
	}
</style>

<style lang="scss">
	.user-invoice-preview-dialog {
		overflow: hidden;
		border-radius: 8px;
		box-shadow: 0 18px 50px rgba(31, 45, 61, 0.2);

		.el-dialog__header {
			padding: 16px 22px;
			background: #fff;
			border-bottom: 1px solid #ebeef5;
		}

		.el-dialog__headerbtn {
			top: 20px;
			right: 20px;
			width: 28px;
			height: 28px;
			border-radius: 50%;
			transition: background-color 0.2s;

			&:hover {
				background: #f2f3f5;
			}

			.el-dialog__close {
				color: #606266;
				font-size: 18px;
			}
		}

		.el-dialog__body {
			padding: 16px;
			background: #f4f6f8;
		}

		.el-dialog__footer {
			padding: 12px 20px;
			background: #fff;
			border-top: 1px solid #ebeef5;
		}
	}

	@media (max-width: 960px) {
		.user-invoice-preview-dialog {
			width: calc(100vw - 32px) !important;
		}
	}
</style>
